import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { matchRange } from '../fs/speedSearch';

/** How long (ms) without a captured keystroke before the query auto-clears. */
export const SPEED_SEARCH_IDLE_MS = 2000;

/** Result of feeding a keydown to {@link UseSpeedSearch.handleKey}. */
export type SpeedSearchKeyResult =
  | { handled: false }
  | { handled: true; focusId?: string };

export interface UseSpeedSearchOptions<T> {
  /** Rows to search, in linear (display) order. */
  items: readonly T[];
  /** Stable identity of a row. */
  getId: (item: T) => string;
  /** The text to match + highlight. */
  getLabel: (item: T) => string;
  /** The caller-owned focused row id (`null` = none). */
  focusedId: string | null;
  /** Called on query change when focus should move (see focus rules below). */
  onJumpToMatch: (id: string) => void;
}

export interface UseSpeedSearch {
  query: string;
  active: boolean;
  /** Ids of matching rows, in `items` order. */
  matches: string[];
  matchSet: Set<string>;
  /** Matched `[start,end)` of a label, or null. */
  rangeFor: (label: string) => [number, number] | null;
  handleKey: (e: KeyboardEvent<HTMLElement>) => SpeedSearchKeyResult;
  reset: () => void;
}

/**
 * Node-agnostic IntelliJ-style speed search. Owns the query string + idle-reset
 * timer and derives the match set from `items`. Does NOT own focus — it returns
 * intents and the caller applies the moves.
 *
 * Focus rules:
 *   - On query change (type / backspace): keep focus if the focused row still
 *     matches; else jump to the first match; else leave focus put. Keyed on
 *     `query` only, so a list change (e.g. expand) never re-jumps.
 *   - Up/Down while active + matches exist: cycle to the next/previous match
 *     (wrap), returned via `handleKey`.
 */
export function useSpeedSearch<T>({
  items,
  getId,
  getLabel,
  focusedId,
  onJumpToMatch,
}: UseSpeedSearchOptions<T>): UseSpeedSearch {
  const [query, setQuery] = useState('');
  const active = query.length > 0;

  // --- idle-reset timer ---
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearIdle = useCallback((): void => {
    if (idleTimer.current !== null) {
      clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
  }, []);
  const armIdle = useCallback((): void => {
    clearIdle();
    idleTimer.current = setTimeout(() => {
      idleTimer.current = null;
      setQuery('');
    }, SPEED_SEARCH_IDLE_MS);
  }, [clearIdle]);
  useEffect(() => clearIdle, [clearIdle]);

  const reset = useCallback((): void => {
    clearIdle();
    setQuery('');
  }, [clearIdle]);

  // --- derived matches ---
  const matches = useMemo<string[]>(() => {
    if (query.length === 0) return [];
    const out: string[] = [];
    for (const item of items) {
      if (matchRange(getLabel(item), query) !== null) out.push(getId(item));
    }
    return out;
  }, [items, query, getId, getLabel]);

  const matchSet = useMemo(() => new Set(matches), [matches]);

  const rangeFor = useCallback(
    (label: string): [number, number] | null => matchRange(label, query),
    [query],
  );

  // --- query-change focus jump (keyed on `query`, NOT on `items`) ---
  const jumpRef = useRef(onJumpToMatch);
  const matchSetRef = useRef(matchSet);
  const matchesRef = useRef(matches);
  const focusedRef = useRef(focusedId);
  useEffect(() => {
    jumpRef.current = onJumpToMatch;
    matchSetRef.current = matchSet;
    matchesRef.current = matches;
    focusedRef.current = focusedId;
  });
  useEffect(() => {
    if (query.length === 0) return;
    const cur = focusedRef.current;
    if (cur && matchSetRef.current.has(cur)) return;
    const first = matchesRef.current[0];
    if (first !== undefined) jumpRef.current(first);
  }, [query]);

  // --- key handling ---
  const handleKey = useCallback(
    (e: KeyboardEvent<HTMLElement>): SpeedSearchKeyResult => {
      const mod = e.ctrlKey || e.metaKey || e.altKey;

      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && active && matches.length > 0 && !mod) {
        e.preventDefault();
        const cur = focusedId;
        const idx = cur ? matches.indexOf(cur) : -1;
        let nextIdx: number;
        if (e.key === 'ArrowDown') {
          nextIdx = idx < 0 ? 0 : (idx + 1) % matches.length;
        } else {
          nextIdx = idx < 0 ? matches.length - 1 : (idx - 1 + matches.length) % matches.length;
        }
        armIdle();
        return { handled: true, focusId: matches[nextIdx] };
      }

      if (e.key === 'Escape') {
        if (!active) return { handled: false };
        e.preventDefault();
        reset();
        return { handled: true };
      }

      if (e.key === 'Backspace') {
        if (!active) return { handled: false };
        e.preventDefault();
        setQuery((q) => q.slice(0, -1));
        armIdle();
        return { handled: true };
      }

      if (!mod && e.key.length === 1) {
        if (e.key === ' ' && !active) return { handled: false };
        e.preventDefault();
        setQuery((q) => q + e.key);
        armIdle();
        return { handled: true };
      }

      return { handled: false };
    },
    [active, matches, focusedId, armIdle, reset],
  );

  return { query, active, matches, matchSet, rangeFor, handleKey, reset };
}
